import React from 'react';
import {
    Card,
    CardMedia,
    Typography,
    IconButton,
    Box,
    Skeleton,
    CardActionArea,
    Snackbar,
    Alert,
} from '@mui/material';
import { Bookmark, BookmarkBorder, Star, LocationOn } from '@mui/icons-material';
import { styled } from '@mui/system';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { addToWishlist, removeFromWishlist } from '../services/api';
import { useAuth } from '../context/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.15s ease-in-out',
    '&:hover': { transform: 'scale3d(1.02, 1.02, 1)' },
    borderRadius: '8px',
    position: 'relative',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.04)',
    overflow: 'hidden',
}));

const StyledCardMedia = styled(CardMedia)({
    aspectRatio: '1/1',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    // height: '149px',
});

const HeartIconButton = styled(IconButton)(({ theme, active }) => ({
    position: 'absolute',
    top: '10px',
    right: '10px',
    color: active ? '#3AB961' : '#FFFFFF',
    padding: '6px',
    zIndex: 1,
    '& .MuiSvgIcon-root': {
        fontSize: '20px',
    },
}));

const RatingBadge = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: '6px',
    left: '11px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#212121',
    borderRadius: '14px',
    paddingHorizontal: '8px',
    paddingVertical: '4px',
    padding: '4px 8px',
    justifyContent: 'center',
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
    padding: '11px',
    display: 'flex',
    flexDirection: 'column',
    height: '75px',
    justifyContent: 'space-between',
}));

const LocationPriceRow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const LocationWrapper = styled(Box)({
    display: 'flex',
    alignItems: 'center',
});

// Format rating: display as integer if rating is whole, otherwise with one decimal
const formatRating = (rating) => {
    const parsedRating = parseFloat(rating);
    if (!isNaN(parsedRating)) {
        return parsedRating % 1 === 0 ? parsedRating.toFixed(0) : parsedRating.toFixed(1);
    }
    return '5';
};

const ListingCard = ({
    listing,
    isWishlisted,
    onWishlistUpdate,
    showWishlist = true,
    dateRange = [new Date(), new Date(Date.now() + 86400000)],
}) => {
    const { currentUser } = useAuth();
    const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
    const [wishlistLoading, setWishlistLoading] = React.useState(false);
    const formatDateToLocalYYYYMMDD = (date) => {
        if (!(date instanceof Date)) return undefined;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };


    const handleWishlistToggle = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!currentUser) {
            setSnackbar({
                open: true,
                message: 'Please log in to add items to your wishlist',
                severity: 'warning'
            });
            return;
        }

        if (wishlistLoading) return;

        setWishlistLoading(true);
        try {
            if (isWishlisted) {
                await removeFromWishlist(listing.id);
            } else {
                await addToWishlist(listing.id);
            }
            if (onWishlistUpdate) {
                onWishlistUpdate(listing.id, !isWishlisted);
            }
        } catch (error) {
            console.error('Error updating wishlist:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update wishlist. Please try again.',
                severity: 'error'
            });
        } finally {
            setWishlistLoading(false);
        }
    };

    return (
        <StyledCard>
            <CardActionArea
                component={Link}
                to={{
                    pathname: `/listings/${listing.uuid}`,
                    search: `?${new URLSearchParams({
                        startDate: formatDateToLocalYYYYMMDD(dateRange?.[0] || new Date()),
                        endDate: formatDateToLocalYYYYMMDD(dateRange?.[1] || new Date(Date.now() + 86400000))
                    }).toString()}`
                }}
                sx={{ height: '100%' }}
            >
                <Box position="relative">
                    {listing.images && listing.images.length > 0 ? (
                        <StyledCardMedia
                            component="img"
                            image={listing.images[0].image_url}
                            alt={listing.title}
                        />
                    ) : (
                        <Skeleton
                            variant="rectangular"
                            sx={{
                                height: '149px',
                                borderTopLeftRadius: '8px',
                                borderTopRightRadius: '8px',
                            }}
                        />
                    )}
                    {showWishlist && (
                        <HeartIconButton
                            active={isWishlisted ? 1 : 0}
                            onClick={handleWishlistToggle}
                            size="small"
                            disabled={wishlistLoading}
                        >
                            {isWishlisted ? <Bookmark /> : <BookmarkBorder />}
                        </HeartIconButton>
                    )}
                    {listing.average_rating > 0 && (
                        <RatingBadge>
                            <Star sx={{ fontSize: '12px', color: '#3AB961' }} />
                            <Typography
                                variant="caption"
                                sx={{
                                    marginLeft: '2px',
                                    fontSize: '11px',
                                    color: '#F6F8FA',
                                    fontWeight: 400
                                }}
                            >
                                {formatRating(listing.average_rating)}
                            </Typography>
                        </RatingBadge>
                    )}

                </Box>

                <ContentWrapper>
                    <Typography
                        variant="subtitle1"
                        component="h3"
                        sx={{
                            fontSize: '17px',
                            lineHeight: '17px',
                            fontWeight: 500,
                            color: '#212121',
                            marginBottom: '4px',
                            height: '34px',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {listing.title || 'Untitled Listing'}
                    </Typography>

                    <LocationPriceRow>
                        <LocationWrapper>
                            <LocationOn sx={{ fontSize: '12px', color: '#767676' }} />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '13px',
                                    lineHeight: '13px',
                                    color: '#767676',
                                    marginLeft: '3px',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '120px',
                                }}
                            >
                                {listing.location || 'New York, NY'}
                            </Typography>
                        </LocationWrapper>

                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontSize: '12.5px',
                                lineHeight: '12.5px',
                                fontFamily: 'Filson Soft, sans-serif',
                                fontWeight: 500,
                                color: '#34A853',
                                '& span': {
                                    fontSize: '14.5px',
                                    lineHeight: '14.5px',
                                    color: '#34A853',
                                    fontWeight: 500,
                                }
                            }}
                        >
                            ${typeof listing.price === 'number' ? listing.price.toFixed(2) : listing.price}<span>/day</span>
                        </Typography>
                    </LocationPriceRow>
                </ContentWrapper>
            </CardActionArea>
            {showWishlist && (
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            )}
        </StyledCard>
    );
};

ListingCard.propTypes = {
    dateRange: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
    listing: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        title: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        images: PropTypes.arrayOf(
            PropTypes.shape({
                image_url: PropTypes.string.isRequired
            })
        ),
        average_rating: PropTypes.number,
        total_reviews: PropTypes.number,
        owner_province: PropTypes.string,
        available_dates: PropTypes.string,
    }).isRequired,
    isWishlisted: PropTypes.bool,
    onWishlistUpdate: PropTypes.func,
    showWishlist: PropTypes.bool,
};

ListingCard.defaultProps = {
    isWishlisted: false,
    onWishlistUpdate: () => { },
    showWishlist: true,
    dateRange: [new Date(), new Date(Date.now() + 86400000)],
};

export default ListingCard;